/** @format */
import React, { useMemo, useState, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import {
  safeCurrentLanguage,
  safeLanguageUrlPrefix,
} from '@services/drupal-services';
import { frCaBlog } from '@pages/newsroom/newsroom';
import Loader from '@components/loader';

const BlogsFeed = lazy(
  () => import(/* webpackChunkName: "blogs-list" */ './blogs')
);

const NewsFeed = lazy(
  () => import(/* webpackChunkName: "news-list" */ './news')
);

const EventsFeed = lazy(
  () => import(/* webpackChunkName: "events-list" */ './events')
);

/**
 * Build Two Column Paragraph.
 */
export const feeds = (): void => {
  const blocks: NodeListOf<HTMLElement> =
    document.querySelectorAll('.varian-block');
  if (blocks.length > 0) {
    blocks.forEach((list: HTMLElement): void => {
      const { limit, blockType } = list.dataset;
      const root = createRoot(list);
      root.render(
        <Suspense fallback={<Loader />}>
          <Feed limit={limit} blockType={blockType} />
        </Suspense>
      );
    });
  }
};

/**
 * Get List Api URL.
 */
const getListApiUrl = (type: string, limit: number): string => {
  let url = '';
  switch (type) {
    case 'events-home-page':
      url = `/${safeLanguageUrlPrefix()}rest-api/varian-events-data?_format=json&limit=${limit}`;
      break;
    case 'featured-event-block':
      url = `/${safeLanguageUrlPrefix()}rest-api/varian-events-data?_format=json&limit=${limit}`;
      break;
    case 'blogs-home-page':
      url = `/${safeLanguageUrlPrefix()}rest-api/varian-blog-data?_format=json&limit=${limit}`;
      break;
    case 'news-home-page':
      url = `/${safeLanguageUrlPrefix()}rest-api/varian-news-data?_format=json&limit=${limit}`;
      break;
  }
  return url;
};

/**
 * Feed Types.
 */
export type NewsItem = {
  nid: string;
  title: string;
  date_release: string;
  node_alias: string;
};

export type BlogItem = {
  nid: string;
  title: string;
  date_release: string;
  widen_image_id: string;
  widen_image_alt_text: string;
  node_alias: string;
};

export type EventItem = {
  nid: string;
  title: string;
  date_start: string;
  date_end: string;
  event_location: string;
  linkaway_uri: string;
  linkaway_title: string;
  date_group: string;
  linkaway_uri_new_window: string;
  edit_link: string;
};

type ListData = {
  events?: EventItem[];
  blogs?: BlogItem[];
  news?: NewsItem[];
};

/**
 * Feed.
 *
 * @param {number} limit
 *   Number of items to show.
 * @param {string} blockType
 *   Block placement for the current feed.
 */
const Feed: React.FC<{
  limit: number;
  blockType: string;
}> = ({ limit, blockType }) => {
  const [listData, setListData] = useState<ListData | null>(null);
  const safeLimit = limit ?? 3;
  const url = getListApiUrl(blockType, safeLimit);

  /**
   * Fetch data.
   */
  useMemo(() => {
    axios
      .get(url)
      .then((response) => {
        if (
          safeCurrentLanguage() === 'fr-ca' &&
          blockType === 'blogs-home-page' &&
          response.data.blogs.length > 0
        ) {
          response.data.blogs.unshift(frCaBlog);
          response.data.blogs.pop();
        }
        setListData(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [url, blockType]);

  /**
   * Render.
   */
  if (listData) {
    if (
      (blockType === 'events-home-page' ||
        blockType === 'featured-event-block') &&
      listData?.events
    ) {
      return <EventsFeed data={listData?.events} />;
    } else if (blockType === 'news-home-page' && listData?.news) {
      return <NewsFeed data={listData?.news} />;
    } else if (blockType === 'blogs-home-page' && listData?.blogs) {
      return <BlogsFeed data={listData?.blogs} />;
    }
  }
  return null;
};
